import __decorate from "../_virtual/_@oxc-project_runtime@0.147.0/helpers/esm/decorate.js";
import "../pd-popup-dialog.js";
import { BUTTON_KEY_CANCEL, BUTTON_KEY_CONFIRM, EVENT_CANCEL } from "../pd-confirm-dialog/PdConfirmDialog.js";
import { LitElement, css, html } from "lit";
import { property, query, state } from "lit/decorators.js";
import "@progressive-development/pd-forms/pd-form-container";
//#region src/pd-form-dialog/PdFormDialog.ts
/**
* PdFormDialog - Abstract base class for form dialogs with validation
*
* Provides a consistent pattern for form dialogs with:
* - Form validation integration (pd-form-container)
* - Save button disabled until form is valid
* - Double-submit prevention (configurable timeout)
* - Standardized event emission with form data
*
* @example
* ```typescript
* interface ContactFormData {
*   name: string;
*   email: string;
* }
*
* @customElement("contact-form-dialog")
* export class ContactFormDialog extends PdFormDialog<ContactFormData> {
*   protected _dialogTitle = "Contact Us";
*   protected _formData: ContactFormData = { name: "", email: "" };
*
*   protected _isValidFormData(): boolean {
*     return this._formData.email.includes("@");
*   }
*
*   protected _renderContent() {
*     return html`
*       <pd-input label="Name" @input=${(e) => this._formData.name = e.target.value}></pd-input>
*       <pd-input label="Email" @input=${(e) => this._formData.email = e.target.value}></pd-input>
*     `;
*   }
* }
* ```
*/
/** Default timeout in ms to prevent double-submit */
var DEFAULT_SUBMIT_DISABLE_TIMEOUT = 5e3;
/** Event name fired when form is submitted with valid data */
var EVENT_FORM_SUBMIT = "pd-form-submit";
var PdFormDialog = class extends LitElement {
	static {
		this.styles = css`
    :host {
      display: block;
      --pd-popup-max-width: var(--pd-form-dialog-max-width, 700px);
    }

    @media (max-width: 767px) {
      :host {
        --pd-popup-max-width: 95%;
      }
    }
  `;
	}
	constructor() {
		super();
		this.saveButtonText = "Save";
		this.cancelButtonText = "Cancel";
		this.showRequiredFieldInfo = true;
		this.submitDisableTimeout = DEFAULT_SUBMIT_DISABLE_TIMEOUT;
		this.closeByEscape = false;
		this.closeByBackdrop = false;
		this.blockScroll = false;
		this._validForm = false;
		this._buttons = [];
		this._scrollBlocked = false;
		this._initializeButtons();
	}
	connectedCallback() {
		super.connectedCallback();
		if (this.blockScroll && !this._scrollBlocked) {
			document.documentElement.style.overflow = "hidden";
			document.body.style.overflow = "hidden";
			this._scrollBlocked = true;
		}
	}
	disconnectedCallback() {
		super.disconnectedCallback();
		if (this._scrollBlocked) {
			document.documentElement.style.overflow = "";
			document.body.style.overflow = "";
			this._scrollBlocked = false;
		}
	}
	render() {
		return html`
      <pd-popup-dialog
        @submit-button-clicked="${this._handleButtonClick}"
        title="${this._dialogTitle}"
        .buttons="${this._getButtons()}"
        ?closeByEscape="${this.closeByEscape}"
        ?closeByBackdrop="${this.closeByBackdrop}"
      >
        <pd-form-container
          slot="content"
          ?requiredFieldInfo="${this.showRequiredFieldInfo}"
          commonError="${this._validationError || ""}"
          @pd-form-change="${this._handleFormChange}"
        >
          ${this._renderContent()}
        </pd-form-container>
      </pd-popup-dialog>
    `;
	}
	/** Reset the form to initial state */
	reset() {
		this._formContainer?.reset();
		this._validForm = false;
		this._validationError = void 0;
	}
	/**
	* Handle button click events.
	* Can be overridden for custom behavior.
	*/
	_handleButtonClick(e) {
		if (e.detail.button === "confirm" && this._validForm) {
			const formData = this._formContainer.getValues();
			this._disableSaveButtonTemporarily();
			this.dispatchEvent(new CustomEvent(EVENT_FORM_SUBMIT, {
				detail: formData,
				bubbles: true,
				composed: true
			}));
		} else this.dispatchEvent(new CustomEvent(EVENT_CANCEL, {
			bubbles: true,
			composed: true
		}));
	}
	_initializeButtons() {
		this._buttons = [{
			key: BUTTON_KEY_CANCEL,
			name: this.cancelButtonText
		}, {
			key: BUTTON_KEY_CONFIRM,
			name: this.saveButtonText,
			disabled: true,
			primary: true
		}];
	}
	_getButtons() {
		return [{
			...this._buttons[0],
			name: this.cancelButtonText
		}, {
			...this._buttons[1],
			name: this.saveButtonText,
			disabled: !this._validForm
		}];
	}
	_handleFormChange(e) {
		this._validForm = e.detail.overallValidity && this._isValidFormData();
		e.stopPropagation();
	}
	_disableSaveButtonTemporarily() {
		this._validForm = false;
		window.setTimeout(() => {
			this._validForm = true;
		}, this.submitDisableTimeout);
	}
};
__decorate([property({ type: String })], PdFormDialog.prototype, "saveButtonText", void 0);
__decorate([property({ type: String })], PdFormDialog.prototype, "cancelButtonText", void 0);
__decorate([property({ type: Boolean })], PdFormDialog.prototype, "showRequiredFieldInfo", void 0);
__decorate([property({ type: Number })], PdFormDialog.prototype, "submitDisableTimeout", void 0);
__decorate([property({ type: Boolean })], PdFormDialog.prototype, "closeByEscape", void 0);
__decorate([property({ type: Boolean })], PdFormDialog.prototype, "closeByBackdrop", void 0);
__decorate([property({ type: Boolean })], PdFormDialog.prototype, "blockScroll", void 0);
__decorate([state()], PdFormDialog.prototype, "_validForm", void 0);
__decorate([state()], PdFormDialog.prototype, "_validationError", void 0);
__decorate([state()], PdFormDialog.prototype, "_buttons", void 0);
__decorate([query("pd-form-container")], PdFormDialog.prototype, "_formContainer", void 0);
//#endregion
export { EVENT_FORM_SUBMIT, PdFormDialog };
